import {createClient, SupabaseClient} from '@supabase/supabase-js';
import {logEvent} from '/src/logger';
import {supabaseAuthStorageKey, supabaseUrl, supabaseAnonKey} from '/src/supabase-config';
import {isAuthEmailPasswordRecoveryMode} from '/src/components/layout/AuthEmailPassword';
import {buildBuyNowCheckoutPath} from '/src/components/pricing/pricing.logic';

export {supabaseUrl};

export const supabase: SupabaseClient = LOCAL_MODE
    ? (createStub() as unknown as SupabaseClient)
    : createClient(supabaseUrl, supabaseAnonKey, {
          auth: {
              flowType: 'pkce',
              storageKey: supabaseAuthStorageKey,
          },
      });

// Redirect to the app only when a sign-in lands on the login page and is not the
// password-recovery flow, which needs to stay on /login to collect the new password.
export function shouldRedirectAfterSignIn(event: string, currentPath: string, search: string, hash: string): boolean {
    return event === 'SIGNED_IN' && currentPath === '/login' && !isAuthEmailPasswordRecoveryMode(search, hash);
}

// Registered explicitly from app startup rather than on import, so importing this module
// does not pull in the router or install a global listener. `getCurrentPath` is injected
// for the same reason.
export function initAuthRedirect(getCurrentPath: () => string): void {
    supabase.auth.onAuthStateChange((event) => {
        setTimeout(async () => {
            if (shouldRedirectAfterSignIn(event, getCurrentPath(), window.location.search, window.location.hash)) {
                logEvent('signed_in');
                window.location.href = '/';
            }
        }, 0);
    });
}

export async function signInWithGithub(isBuyNow?: string, offerCode?: string, redirectPath?: string) {
    const {data, error} = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
            redirectTo: getOAuthRedirectUrl(isBuyNow, offerCode, redirectPath),
        },
    });
}

export async function signOut() {
    logEvent('signed_out');
    await supabase.auth.signOut();
}

export async function getSession() {
    if (typeof window === 'undefined') {
        return null;
    }

    const url = new URL(window.location.href);
    const authCode = url.searchParams.get('code');

    if (authCode) {
        const {data, error} = await supabase.auth.exchangeCodeForSession(authCode);

        if (!error) {
            url.searchParams.delete('code');
            history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
            return data.session;
        }
    }

    if (window.location.hash) {
        const hashParams = new URLSearchParams(window.location.hash.slice(1));
        const access_token = hashParams.get('access_token');
        const refresh_token = hashParams.get('refresh_token');

        if (access_token && refresh_token) {
            const {data, error} = await supabase.auth.setSession({access_token, refresh_token});
            if (!error) {
                history.replaceState({}, document.title, `${url.pathname}${url.search}`);
                return data.session;
            }
        }
    }

    const {
        data: {session},
    } = await supabase.auth.getSession();
    return session;
}

export async function signInWithGoogle(isBuyNow?: string, offerCode?: string, redirectPath?: string) {
    const {data, error} = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
            redirectTo: getOAuthRedirectUrl(isBuyNow, offerCode, redirectPath),
        },
    });
}

function getAppUrlBase(): string | undefined {
    return import.meta.env.VITE_APP_URL || (typeof window !== 'undefined' ? window.location.origin : undefined);
}

function getOAuthRedirectUrl(isBuyNow?: string, offerCode?: string, redirectPath?: string) {
    const appUrlBase = getAppUrlBase();

    if (typeof redirectPath === 'string' && redirectPath.startsWith('/') && !redirectPath.startsWith('//')) {
        return appUrlBase ? new URL(redirectPath, appUrlBase).toString() : redirectPath;
    }
    if (!isBuyNow) return appUrlBase;

    const buyNowPath = buildBuyNowCheckoutPath(isBuyNow, offerCode);
    return appUrlBase ? new URL(buyNowPath, appUrlBase).toString() : buyNowPath;
}

export async function signInWithEmail(email: string) {
    const {data, error} = await supabase.auth.signInWithOtp({
        email: email,
    });
    return {
        result: data,
        error,
    };
}

export async function signInWithPassword(email: string, password: string) {
    const {data, error} = await supabase.auth.signInWithPassword({
        email,
        password,
    });

    return {
        result: data,
        error,
    };
}

export async function signUpWithPassword(email: string, password: string) {
    const {data, error} = await supabase.auth.signUp({
        email,
        password,
        options: {
            emailRedirectTo: `${import.meta.env.VITE_APP_URL}/login`,
        },
    });

    return {
        result: data,
        error,
    };
}

export async function sendPasswordReset(email: string) {
    const {data, error} = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${import.meta.env.VITE_APP_URL}/login?mode=recovery`,
    });

    return {
        result: data,
        error,
    };
}

export async function updatePassword(password: string) {
    const {data, error} = await supabase.auth.updateUser({
        password,
    });

    return {
        result: data,
        error,
    };
}

export function onPasswordRecovery(callback: () => void) {
    const {
        data: {subscription},
    } = supabase.auth.onAuthStateChange((event) => {
        if (event === 'PASSWORD_RECOVERY') {
            callback();
        }
    });

    return () => {
        subscription?.unsubscribe();
    };
}

function createStub() {
    return {
        auth: {
            onAuthStateChange: () => {},
            getSession: () => Promise.resolve({data: {session: null}}),
            exchangeCodeForSession: () => Promise.resolve({data: {session: null}, error: null}),
            signInWithPassword: () => Promise.resolve({data: null, error: null}),
            signUp: () => Promise.resolve({data: null, error: null}),
            resetPasswordForEmail: () => Promise.resolve({data: null, error: null}),
            updateUser: () => Promise.resolve({data: null, error: null}),
        },
    };
}
